import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import useAppointmentStore from "../store/useAppointmentStore";
import useAuth from "../store/useAuth";
import showToast from "../utils/toastHelper";

export const SpecializationURLRedirectHandler = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useParams<{
    specializationId?: string;
    specializationName?: string;
    facilityId?: string;
    facilityName?: string;
  }>();
  
  const { 
    setSelectedSpecialization, 
    setSelectedFacility 
  } = useAppointmentStore();
  const { isAuthenticated } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string | null>(null);

  useEffect(() => {
    const processSpecializationFlow = async () => {
      setIsProcessing(true);
      setError(null);
      
      try {        // First check if we're using the new URL format with path parameters
        if (params.specializationId) {
          setDebugInfo(`specializationId: ${params.specializationId}, facilityId: ${params.facilityId || 'none'}`);
          
          // Process the specialization ID from path params
          const specializationId = params.specializationId;
          const facilityId = params.facilityId;
          
          // Setup the specialization QR flow
          localStorage.setItem("specializationQRFlow", "active");
          setSelectedSpecialization(specializationId.toString());
          sessionStorage.setItem("qrSpecializationId", specializationId.toString());
          localStorage.setItem("specializationSelected", "true");
          localStorage.setItem("fromSpecializationQR", "true");
          sessionStorage.setItem("qrRedirectTarget", "/doctors");
          
          // If facility ID is provided, also store that
          if (facilityId) {
            setSelectedFacility(parseInt(facilityId));
            sessionStorage.setItem("pendingFacility", facilityId.toString());
            sessionStorage.setItem("qrFacilityId", facilityId.toString());
            localStorage.setItem("hospitalSelected", "true");
          }
            // Save the exact path for post-auth redirect
          const currentPath = window.location.pathname;
          localStorage.setItem("intendedPath", currentPath);
          
          // Also save it to sessionStorage for new users
          sessionStorage.setItem("originalIntendedPath", currentPath);
          
          // Store specialization and facility IDs separately to ensure they're available
          // regardless of redirect path
          sessionStorage.setItem("pendingSpecialization", specializationId.toString());
          if (facilityId) {
            sessionStorage.setItem("pendingFacility", facilityId.toString());
          }
          
          // Mark the flow as complete
          localStorage.setItem("specializationQRComplete", "true");
        }
        // Otherwise check for token parameter (backward compatibility)
        else {
          const token = searchParams.get("token");
          if (!token) {
            console.error("Missing token parameter in QR redirect");
            setError("Missing parameters for specialization");
            setIsProcessing(false);
            showToast("Invalid QR code or link", "error");
            setTimeout(() => navigate("/specializations", { replace: true }), 1500);
            return;
          }
            // Decode the token to get payload
          const decodedPayload = JSON.parse(atob(decodeURIComponent(token)));
          
          // Extract IDs from payload
          const specializationId = decodedPayload.s;
          const facilityId = decodedPayload.f; // This might be undefined
          
          if (!specializationId) {
            console.error("Missing specialization ID in token payload:", decodedPayload);
            setError("Invalid specialization data");
            setIsProcessing(false);
            return;
          }
          
          setDebugInfo(`Legacy token - specializationId: ${specializationId}, facilityId: ${facilityId || 'none'}`);
          
          localStorage.setItem("specializationQRFlow", "active");
          setSelectedSpecialization(specializationId.toString());
          sessionStorage.setItem("qrSpecializationId", specializationId.toString());
          localStorage.setItem("specializationSelected", "true");
            // If facility ID is provided, also store that
          if (facilityId) {
            setSelectedFacility(parseInt(facilityId.toString()));
            sessionStorage.setItem("pendingFacility", facilityId.toString());
            sessionStorage.setItem("qrFacilityId", facilityId.toString());
            localStorage.setItem("hospitalSelected", "true");
          }
            localStorage.setItem("fromSpecializationQR", "true");
          sessionStorage.setItem("qrRedirectTarget", "/doctors");
          
          // For token-based URLs, construct an equivalent path-based URL for redirection
          const pathBasedURL = `/specializations/${specializationId}/${encodeURIComponent(decodedPayload.n || 'specialization')}`;
          
          // Add facility to the path if available
          const fullPathURL = facilityId 
            ? `${pathBasedURL}/${facilityId}/${encodeURIComponent(decodedPayload.fn || 'selected-facility')}` 
            : pathBasedURL;
          
          // Save this path for post-auth redirect
          localStorage.setItem("intendedPath", fullPathURL);
          
          // Also save to sessionStorage for new users
          sessionStorage.setItem("originalIntendedPath", fullPathURL);
          
          localStorage.setItem("specializationQRComplete", "true");
        }
          // Handle redirection based on authentication state
        if (isAuthenticated) {
         
          navigate("/doctors", { 
            state: { 
              specializationId: params.specializationId || sessionStorage.getItem("qrSpecializationId"),
              facilityId: params.facilityId || sessionStorage.getItem("qrFacilityId"),
              fromQR: true
            },
            replace: true
          });
        } else {         
        
          const currentPath = window.location.pathname;
          
          // Store in multiple locations to ensure it's not lost
          localStorage.setItem("intendedPath", currentPath);
          sessionStorage.setItem("originalIntendedPath", currentPath);
          sessionStorage.setItem("storedSpecializationPath", currentPath);
          
          const specId = params.specializationId || sessionStorage.getItem("qrSpecializationId");
          if (specId) {
            sessionStorage.setItem("pendingSpecialization", specId);
          }
          
          const facId = params.facilityId || sessionStorage.getItem("qrFacilityId");
          if (facId) {
            sessionStorage.setItem("pendingFacility", facId);
          }
          
          localStorage.setItem("appInitialized", "initializing");
          navigate("/get-user", { 
            state: { 
              fromSpecializationQR: true,
              specializationId: params.specializationId || sessionStorage.getItem("qrSpecializationId"),
              facilityId: params.facilityId || sessionStorage.getItem("qrFacilityId")
            },
            replace: true 
          });
        }
      } catch (error) {
        console.error("Error processing specialization redirect:", error);
        setError("Invalid URL format");
        setIsProcessing(false);
        showToast("Invalid QR code or link format", "error");
        // Redirect to specializations page after a short delay
        setTimeout(() => navigate("/specializations", { replace: true }), 1500);
      }
    };

    processSpecializationFlow();
  }, [
    searchParams, 
    params, 
    navigate, 
    setSelectedSpecialization, 
    setSelectedFacility, 
    isAuthenticated
  ]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-red-500 text-xl mb-4">Error: {error}</div>
        {debugInfo && <div className="text-gray-500 text-sm mb-4">Debug: {debugInfo}</div>}
        <div className="text-gray-500">Redirecting to specializations page...</div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="mb-4 w-12 h-12 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
        <div className="text-gray-700 font-medium mb-2">Processing Specialization</div>
        <div className="text-gray-500 text-sm">Please wait while we prepare your experience...</div>
        {debugInfo && <div className="text-gray-400 text-xs mt-8">Debug: {debugInfo}</div>}
      </div>
    );
  }

  return null;
};
